import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useSearchParams } from "react-router-dom";
import {
  FiChevronDown,
  FiClipboard,
  FiCreditCard,
  FiDollarSign,
  FiGrid,
  FiHash,
  FiLayers,
  FiLogOut,
  FiMapPin,
  FiMonitor,
  FiPackage,
  FiSettings,
  FiShoppingCart,
} from "react-icons/fi";
import Navbar from "../Components/Navbar";
import { usePmsTheme } from "../Components/PmsThemeContext";
import useIsMobile from "../hooks/useIsMobile";
import axios from "../api/axiosInstance";
import { readStoredUser, setActiveBusinessUnitSession } from "../utils/sessionUser";

export default function PosPanel() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const { language } = usePmsTheme();
  const commonCopy = language.messages.common;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarPinned, setIsSidebarPinned] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [businessUnitName, setBusinessUnitName] = useState("");
  const [sessionUser, setSessionUser] = useState(() => readStoredUser());

  const currentUser = sessionUser || {};
  const businessUnitId =
    searchParams.get("business_unit_id") || currentUser?.active_business_unit_id || "1";

  const withBusinessUnit = useCallback(
    (path) => `${path}?business_unit_id=${encodeURIComponent(String(businessUnitId))}`,
    [businessUnitId]
  );

  const isSidebarExpanded = isSidebarPinned || isSidebarOpen;

  const handleSidebarEnter = () => {
    setIsSidebarOpen(true);
  };

  const handleSidebarLeave = () => {
    if (isSidebarPinned) return;
    setIsSidebarOpen(false);
  };

  const handlePinToggle = () => {
    setIsSidebarPinned((prev) => {
      const next = !prev;
      setIsSidebarOpen(next);
      return next;
    });
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem("palantir_token");
    localStorage.removeItem("palantir_user");
    window.location.href = "/";
  }, []);

  useEffect(() => {
    const syncSessionFromStorage = () => {
      setSessionUser(readStoredUser());
    };

    window.addEventListener("palantir-session-updated", syncSessionFromStorage);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionFromStorage);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const syncActiveBusinessUnit = async () => {
      const requestedBu = Number(businessUnitId || 0);
      const activeBu = Number(currentUser?.active_business_unit_id || 0);

      if (!currentUser?.id || !requestedBu || requestedBu === activeBu) {
        return;
      }

      try {
        const nextUser = await setActiveBusinessUnitSession(currentUser.id, requestedBu);
        if (!cancelled) {
          setSessionUser(nextUser);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("[PosPanel] Error syncing active business unit:", err);
        }
      }
    };

    syncActiveBusinessUnit();

    return () => {
      cancelled = true;
    };
  }, [businessUnitId, currentUser?.active_business_unit_id, currentUser?.id]);

  useEffect(() => {
    let cancelled = false;

    const loadBusinessUnitName = async () => {
      if (!businessUnitId) return;
      try {
        const res = await axios.get(`/api/business-units/${businessUnitId}`);
        if (!cancelled) {
          setBusinessUnitName(String(res?.data?.name || "").trim());
        }
      } catch (err) {
        if (!cancelled) {
          setBusinessUnitName("");
        }
        console.warn("[PosPanel] Error loading business unit name:", err);
      }
    };

    loadBusinessUnitName();

    return () => {
      cancelled = true;
    };
  }, [businessUnitId]);

  useEffect(() => {
    if (
      location.pathname.startsWith("/pos/products") ||
      location.pathname.startsWith("/pos/classifications") ||
      location.pathname.startsWith("/pos/outlets") ||
      location.pathname.startsWith("/pos/catalogs") ||
      location.pathname.startsWith("/pos/payment-methods") ||
      location.pathname.startsWith("/pos/devices") ||
      location.pathname.startsWith("/pos/tables")
    ) {
      setOpenSection("setup");
      return;
    }
    if (
      location.pathname.startsWith("/pos/sales") ||
      location.pathname.startsWith("/pos/orders") ||
      location.pathname.startsWith("/pos/customer-credit")
    ) {
      setOpenSection(null);
    }
  }, [location.pathname]);

  const menuItems = useMemo(
    () => [
      {
        key: "sales",
        label: "Sales",
        meta: "Daily detail",
        to: withBusinessUnit("/pos/sales"),
        Icon: FiShoppingCart,
      },
      {
        key: "orders",
        label: "Orders",
        meta: "Tablet queue",
        to: withBusinessUnit("/pos/orders"),
        Icon: FiClipboard,
      },
      {
        key: "customer-credit",
        label: "Customer credit",
        meta: "Balances & redemptions",
        to: withBusinessUnit("/pos/customer-credit"),
        Icon: FiDollarSign,
      },
      {
        key: "setup",
        label: "Setup",
        meta: "Catalog, tender & devices",
        activePaths: ["/pos/products", "/pos/classifications", "/pos/outlets", "/pos/catalogs", "/pos/payment-methods", "/pos/devices", "/pos/tables"],
        Icon: FiSettings,
        children: [
          {
            key: "outlets",
            label: "Outlets",
            to: withBusinessUnit("/pos/outlets"),
            Icon: FiMapPin,
          },
          {
            key: "products",
            label: "Products",
            to: withBusinessUnit("/pos/products"),
            Icon: FiPackage,
          },
          {
            key: "classifications",
            label: "Classifications",
            to: withBusinessUnit("/pos/classifications"),
            Icon: FiLayers,
          },
          {
            key: "catalogs",
            label: "Catalogs",
            to: withBusinessUnit("/pos/catalogs"),
            Icon: FiGrid,
          },
          {
            key: "payment-methods",
            label: "Payment Methods",
            to: withBusinessUnit("/pos/payment-methods"),
            Icon: FiCreditCard,
          },
          {
            key: "devices",
            label: "Devices",
            to: withBusinessUnit("/pos/devices"),
            Icon: FiMonitor,
          },
          {
            key: "tables",
            label: "Tables & QR",
            to: withBusinessUnit("/pos/tables"),
            Icon: FiHash,
          },
        ],
      },
    ],
    [withBusinessUnit]
  );

  const isPathActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  const renderSubItem = (item, parentKey) => {
    const Icon = item.Icon || FiGrid;
    const targetPath = String(item.to || "").split("?")[0];
    const isActive = isPathActive(targetPath);

    return (
      <NavLink
        key={`${parentKey}-${item.key}`}
        to={item.to}
        title={item.label}
        className={() => `pmsSubItem ${isActive ? "pmsSubItem--active" : ""}`}
      >
        <span className="pmsSubItemIcon" aria-hidden="true">
          <Icon size={14} />
        </span>
        <span className="pmsSubItemLabel">{item.label}</span>
      </NavLink>
    );
  };

  if (isMobile) {
    return (
      <div className="posModule posModuleMobile">
        <Navbar />
        <main className="pmsMain pmsMain--mobile" style={{ paddingTop: "64px", paddingLeft: 8, paddingRight: 8 }}>
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="posModule pmsShell">
      <aside
        className={`pmsSidebar ${isSidebarExpanded ? "pmsSidebar--open" : ""} ${
          isSidebarPinned ? "pmsSidebar--pinned" : ""
        }`}
        aria-label="POS navigation"
        onMouseEnter={handleSidebarEnter}
        onMouseLeave={handleSidebarLeave}
      >
        <div className="pmsSidebarInner">
          <div className="pmsBrand" title="POS Panel">
            <div className="pmsBrandMark">
              <img
                className="pmsBrandLogo"
                src={`${process.env.PUBLIC_URL}/Isotipo_Pilcore_Blanco.svg`}
                alt="Pilcore"
              />
            </div>
            <div className="pmsBrandText">
              <div className="pmsBrandTitle">POS</div>
              <div className="pmsBrandSubtitle">Pilcore</div>
              <div
                className="pmsBrandHotel"
                title={businessUnitName || currentUser?.business_unit_name || commonCopy.businessUnit}
              >
                {businessUnitName || currentUser?.business_unit_name || commonCopy.businessUnit}
              </div>
              <div className="pmsBrandActions">
                <span className="pmsBrandLogoutIcon" aria-hidden="true">
                  <FiLogOut size={11} />
                </span>
                <button
                  type="button"
                  className="pmsBrandLogout"
                  onClick={handleLogout}
                  title={commonCopy.logout}
                >
                  {commonCopy.logout}
                </button>
              </div>
            </div>
            {isSidebarExpanded ? (
              <button
                type="button"
                className={`pmsPinButton ${isSidebarPinned ? "pmsPinButton--active" : ""}`}
                onClick={handlePinToggle}
                title={isSidebarPinned ? "Unpin sidebar" : "Pin sidebar"}
                aria-pressed={isSidebarPinned}
                aria-label={isSidebarPinned ? "Unpin sidebar" : "Pin sidebar"}
              >
                <FiMapPin size={14} />
              </button>
            ) : null}
          </div>

          <nav className="pmsNav">
            {menuItems.map((item) => {
              const Icon = item.Icon || FiGrid;
              const childPaths = item.activePaths || [];
              const targetPath = String(item.to || "").split("?")[0];
              const isActive = item.children?.length
                ? childPaths.some((path) => isPathActive(path))
                : isPathActive(targetPath);

              if (item.children?.length) {
                const isOpen = openSection === item.key;
                return (
                  <div key={item.key} className="pmsNavSection">
                    <button
                      type="button"
                      className={`pmsNavItem ${isActive ? "pmsNavItem--active" : ""}`}
                      aria-expanded={isOpen}
                      onClick={() => setOpenSection((prev) => (prev === item.key ? null : item.key))}
                      title={item.label}
                    >
                      <span className="pmsNavItemIcon" aria-hidden="true">
                        <Icon size={18} />
                      </span>
                      <span className="pmsNavItemText">
                        <span className="pmsNavItemLabel">{item.label}</span>
                        <span className="pmsNavItemMeta">{item.meta}</span>
                      </span>
                      <span className="pmsChevron" aria-hidden="true">
                        <FiChevronDown size={16} />
                      </span>
                    </button>

                    <div className={`pmsSubNav ${isOpen ? "pmsSubNav--open" : ""}`}>
                      {item.children.map((child) => renderSubItem(child, item.key))}
                    </div>
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  title={item.label}
                  className={() => `pmsNavItem ${isActive ? "pmsNavItem--active" : ""}`}
                >
                  <span className="pmsNavItemIcon" aria-hidden="true">
                    <Icon size={18} />
                  </span>
                  <span className="pmsNavItemText">
                    <span className="pmsNavItemLabel">{item.label}</span>
                    <span className="pmsNavItemMeta">{item.meta}</span>
                  </span>
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>

      <main className="pmsMain">
        <Outlet />
      </main>
    </div>
  );
}
